import React, { useRef, useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { 
  FaUsers, FaComments, FaCalendarCheck, FaHeart, FaRegHeart, 
  FaGlobe, FaStar, FaSearch, FaTimes, FaPlus, FaCrown, FaOm, 
  FaSpa, FaLeaf, FaHeartbeat, FaQuestionCircle, FaChild, 
  FaShareAlt, FaPaperPlane, FaCheckCircle, FaChevronDown, 
  FaRegCommentDots, FaTrash, FaPenFancy, FaFireAlt, FaUserCheck,
  FaWhatsapp, FaFacebookF, FaLinkedinIn, FaTelegramPlane,
  FaEnvelope, FaCopy, FaLink
} from "react-icons/fa";
import { FiEye, FiCheck, FiX, FiAlertCircle, FiShare2, FiExternalLink } from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { communityAPI } from "../services/api";

// ─── AUTH STATE HELPER ────────────────────────────────────────────────────────
const getAuthState = () => {
  try {
    const token = localStorage.getItem("divyayoga_token");
    const role = localStorage.getItem("divyayoga_role");
    const user = JSON.parse(localStorage.getItem("divyayoga_user") || "null");
    if (token && user) return { loggedIn: true, role, user, token };
  } catch {}
  return { loggedIn: false, role: null, user: null, token: null };
};

// ─── STAGGER ANIMATIONS ───────────────────────────────────────────────────────
const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 25 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 1, 0.5, 1] } }
};

// ─── 3D MAGNETIC CARD COMPONENT ───────────────────────────────────────────────
function MagCard({ children, className = "", style = {} }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 18 });
  const sy = useSpring(y, { stiffness: 180, damping: 18 });

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * 10);
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * -10);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX: sy, rotateY: sx, transformStyle: "preserve-3d", transformPerspective: 1000, ...style }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── CATEGORY CONSTANTS WITH STANDARD ICONS (ZERO EMOJIS) ─────────────────────
const CATEGORIES = [
  { key: "All", label: "All Discussions", Icon: FaOm, desc: "Explore all topics" },
  { key: "Asanas & Alignment", label: "Asanas & Alignment", Icon: FaChild, desc: "Postures & precision" },
  { key: "Meditation & Pranayama", label: "Meditation & Pranayama", Icon: FaSpa, desc: "Breathwork & stillness" },
  { key: "Nutrition & Ayurveda", label: "Nutrition & Ayurveda", Icon: FaLeaf, desc: "Sattvic living & recipes" },
  { key: "Healing & Recovery", label: "Healing & Recovery", Icon: FaHeartbeat, desc: "Rehabilitation & wellness" },
  { key: "Success Stories", label: "Success Stories", Icon: FaStar, desc: "Milestones & transformations" },
  { key: "Q&A", label: "Q&A Guidance", Icon: FaQuestionCircle, desc: "Seek guidance from teachers" },
];

const SORT_OPTIONS = [
  { key: "latest", label: "Latest Active", desc: "Most recently shared discussions" },
  { key: "popular", label: "Most Popular", desc: "Highest likes from practitioners" },
  { key: "discussed", label: "Most Discussed", desc: "Highest replies and engagement" },
];

// ─── CUSTOM LUXURY SORT DROPDOWN (ZERO DEFAULT SELECT) ────────────────────────
function CustomSortDropdown({ selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeOption = SORT_OPTIONS.find((opt) => opt.key === selected) || SORT_OPTIONS[0];

  return (
    <div className="relative z-50 min-w-[200px]" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-[48px] rounded-2xl border border-[#F4D9B5] bg-white/90 backdrop-blur-xl px-4 flex items-center justify-between text-xs font-semibold text-[#111111] shadow-sm hover:border-[#F29A0C] transition-all duration-300 cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <span className="text-[#888888] font-normal">Sort:</span>
          <span className="text-[#F29A0C] font-bold">{activeOption.label}</span>
        </span>
        <FaChevronDown
          size={11}
          className={`text-[#888888] transition-transform duration-300 ${isOpen ? "rotate-180 text-[#F29A0C]" : ""}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full mt-2 w-[240px] z-[120] rounded-2xl border border-[#F4D9B5] bg-white/98 backdrop-blur-2xl p-2 shadow-[0_25px_60px_rgba(0,0,0,0.18)] space-y-1"
          >
            {SORT_OPTIONS.map((opt) => {
              const isSelected = opt.key === selected;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    onChange(opt.key);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left p-2.5 rounded-xl transition-all duration-200 flex items-center justify-between cursor-pointer ${
                    isSelected
                      ? "bg-[#FFF3E0] text-[#F29A0C] font-bold"
                      : "text-[#333333] hover:bg-[#FFF8EE] hover:text-[#F29A0C]"
                  }`}
                >
                  <div>
                    <p className="text-xs font-semibold leading-none">{opt.label}</p>
                    <p className="text-[10px] text-[#777777] font-normal mt-1">{opt.desc}</p>
                  </div>
                  {isSelected && <FiCheck size={14} className="text-[#F29A0C] shrink-0" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── CUSTOM LUXURY CATEGORY SELECTOR FOR COMPOSER & MODAL ─────────────────────
function CustomCategorySelect({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const creationCategories = CATEGORIES.filter((c) => c.key !== "All");

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeCategory = creationCategories.find((c) => c.key === value) || creationCategories[0];
  const ActiveIcon = activeCategory.Icon;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-[50px] rounded-2xl border border-[#F4D9B5] bg-white/95 px-4 flex items-center justify-between text-xs font-semibold text-[#111111] shadow-sm hover:border-[#F29A0C] transition-all duration-300 cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-xl bg-[#FFF3E0] border border-[#F4D9B5]/70 flex items-center justify-center text-[#F29A0C]">
            <ActiveIcon size={12} />
          </div>
          <div className="text-left">
            <span className="block text-xs font-bold text-[#111111]">{activeCategory.label}</span>
          </div>
        </div>
        <FaChevronDown
          size={11}
          className={`text-[#888888] transition-transform duration-300 ${isOpen ? "rotate-180 text-[#F29A0C]" : ""}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 right-0 top-full mt-2 z-50 rounded-2xl border border-[#F4D9B5] bg-white/95 backdrop-blur-2xl p-2 shadow-[0_20px_50px_rgba(0,0,0,0.15)] max-h-[250px] overflow-y-auto space-y-1"
          >
            {creationCategories.map((cat) => {
              const Icon = cat.Icon;
              const isSelected = cat.key === value;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => {
                    onChange(cat.key);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left p-2.5 rounded-xl transition-all duration-200 flex items-center justify-between cursor-pointer ${
                    isSelected
                      ? "bg-[#FFF3E0] text-[#F29A0C] font-bold"
                      : "text-[#333333] hover:bg-[#FFF8EE] hover:text-[#F29A0C]"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                        isSelected ? "bg-[#F29A0C] text-white" : "bg-[#FFF3E0] text-[#F29A0C]"
                      }`}
                    >
                      <Icon size={12} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold leading-tight">{cat.label}</p>
                      <p className="text-[10px] text-[#777777] font-normal">{cat.desc}</p>
                    </div>
                  </div>
                  {isSelected && <FiCheck size={14} className="text-[#F29A0C] shrink-0" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── MAIN COMMUNITY COMPONENT ─────────────────────────────────────────────────
export default function Community() {
  const navigate = useNavigate();
  const auth = useMemo(() => getAuthState(), []);

  // Community Feed State
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Real API-Driven Stats & Metadata
  const [stats, setStats] = useState({
    activeMembers: 0,
    totalDiscussions: 0,
    totalReplies: 0,
    totalLikes: 0,
    categoryBreakdown: {},
    recentContributors: [],
  });

  // Filters State
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSort, setSelectedSort] = useState("latest");
  const [viewMyPostsOnly, setViewMyPostsOnly] = useState(false);

  // Interactive Comments State
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [commentsMap, setCommentsMap] = useState({});
  const [commentsLoading, setCommentsLoading] = useState({});
  const [replyTextMap, setReplyTextMap] = useState({});
  const [submittingReply, setSubmittingReply] = useState({});

  // Likes tracking state
  const [likedPosts, setLikedPosts] = useState(new Set());

  // Create Discussion Form (Inline & Modal)
  const [createForm, setCreateForm] = useState({
    title: "",
    category: "Asanas & Alignment",
    content: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirmState, setDeleteConfirmState] = useState({ isOpen: false, post: null });
  const [shareModalState, setShareModalState] = useState({ isOpen: false, post: null, copied: false });

  // ─── FETCH COMMUNITY STATS (100% REAL API) ───────────────────────────────────
  const fetchStats = async () => {
    try {
      const res = await communityAPI.getStats();
      if (res.data?.success && res.data.data) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.warn("Could not load community stats from API", err);
    }
  };

  // ─── FETCH DISCUSSIONS (100% REAL API) ───────────────────────────────────────
  const fetchDiscussions = async () => {
    setLoading(true);
    try {
      const res = await communityAPI.getPosts({
        category: selectedCategory,
        search: searchQuery,
        sort: selectedSort,
        page: 1,
        pageSize: 40,
      });

      if (res.data?.success && Array.isArray(res.data.data)) {
        setPosts(res.data.data);
      } else {
        setPosts([]);
      }
    } catch (err) {
      console.error("Error fetching discussions:", err);
      toast.error("Failed to load circle discussions from API.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchDiscussions();
  }, [selectedCategory, selectedSort]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDiscussions();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Auto-scroll to shared discussion from deep-link URL (e.g., ?discussion=12)
  useEffect(() => {
    if (posts.length === 0) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const discussionId = params.get("discussion") || window.location.hash.replace("#discussion-", "").replace("#post-", "");
      if (discussionId) {
        const el = document.getElementById(`discussion-${discussionId}`);
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-[#F29A0C]", "ring-offset-4");
            setTimeout(() => {
              el.classList.remove("ring-2", "ring-[#F29A0C]", "ring-offset-4");
            }, 3500);
          }, 400);
        }
      }
    } catch {}
  }, [posts]);

  // ─── FILTER FOR "MY POSTS" ──────────────────────────────────────────────────
  const displayedPosts = useMemo(() => {
    if (!viewMyPostsOnly || !auth.loggedIn) return posts;
    const currentUserId = auth.user?.id;
    const currentUserName = auth.user?.fullName?.toLowerCase();
    return posts.filter(
      (p) =>
        (currentUserId && p.userId === currentUserId) ||
        (currentUserName && p.authorName?.toLowerCase() === currentUserName)
    );
  }, [posts, viewMyPostsOnly, auth]);

  // ─── TOGGLE LIKE WITH OPTIMISTIC UPDATE ──────────────────────────────────────
  const handleToggleLike = async (postId) => {
    const isCurrentlyLiked = likedPosts.has(postId);

    // Optimistic Update
    setLikedPosts((prev) => {
      const next = new Set(prev);
      if (isCurrentlyLiked) next.delete(postId);
      else next.add(postId);
      return next;
    });

    setPosts((prev) =>
      prev.map((p) => {
        if (p.id === postId) {
          return {
            ...p,
            likesCount: isCurrentlyLiked ? Math.max(0, p.likesCount - 1) : p.likesCount + 1,
          };
        }
        return p;
      })
    );

    try {
      const res = await communityAPI.toggleLike(postId);
      if (res.data?.success) {
        setPosts((prev) =>
          prev.map((p) => (p.id === postId ? { ...p, likesCount: res.data.likesCount } : p))
        );
        fetchStats();
      }
    } catch (err) {
      // Revert on failure
      setLikedPosts((prev) => {
        const next = new Set(prev);
        if (isCurrentlyLiked) next.add(postId);
        else next.delete(postId);
        return next;
      });
      toast.error("Unable to update like.");
    }
  };

  // ─── EXPAND COMMENTS & LOAD THREAD ──────────────────────────────────────────
  const handleToggleComments = async (postId) => {
    if (expandedPostId === postId) {
      setExpandedPostId(null);
      return;
    }

    setExpandedPostId(postId);

    if (!commentsMap[postId]) {
      setCommentsLoading((prev) => ({ ...prev, [postId]: true }));
      try {
        const res = await communityAPI.getComments(postId);
        if (res.data?.success && Array.isArray(res.data.data)) {
          setCommentsMap((prev) => ({ ...prev, [postId]: res.data.data }));
        }
      } catch (err) {
        toast.error("Could not fetch replies for this discussion.");
      } finally {
        setCommentsLoading((prev) => ({ ...prev, [postId]: false }));
      }
    }
  };

  // ─── SUBMIT REPLY ────────────────────────────────────────────────────────────
  const handleSubmitReply = async (postId) => {
    if (!auth.loggedIn) {
      toast.info("Please sign in to reply to circle discussions.");
      navigate("/login");
      return;
    }

    const text = (replyTextMap[postId] || "").trim();
    if (!text) {
      toast.warning("Please enter your reply before submitting.");
      return;
    }

    setSubmittingReply((prev) => ({ ...prev, [postId]: true }));
    try {
      const res = await communityAPI.addComment(postId, { content: text });
      if (res.data?.success && res.data.data) {
        toast.success("Your reply has been shared.");
        setCommentsMap((prev) => ({
          ...prev,
          [postId]: [...(prev[postId] || []), res.data.data],
        }));
        setReplyTextMap((prev) => ({ ...prev, [postId]: "" }));
        setPosts((prev) =>
          prev.map((p) => (p.id === postId ? { ...p, commentsCount: p.commentsCount + 1 } : p))
        );
        fetchStats();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to post reply.");
    } finally {
      setSubmittingReply((prev) => ({ ...prev, [postId]: false }));
    }
  };

  // ─── CREATE NEW DISCUSSION (DIRECT REAL API POST) ────────────────────────────
  const handleCreatePost = async (e) => {
    if (e) e.preventDefault();
    if (!auth.loggedIn) {
      toast.info("Please sign in or register to publish a discussion.");
      navigate("/login");
      return;
    }

    if (!createForm.title.trim()) {
      toast.warning("Please enter a discussion title.");
      return;
    }

    if (!createForm.content.trim()) {
      toast.warning("Please enter your thoughts or question.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await communityAPI.createPost(createForm);
      if (res.data?.success && res.data.data) {
        toast.success("Discussion published to the Universal Sangha!");
        setCreateForm({
          title: "",
          category: "Asanas & Alignment",
          content: "",
        });
        setIsModalOpen(false);
        // Prepend directly into feed
        setPosts((prev) => [res.data.data, ...prev]);
        fetchStats();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create discussion.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── DELETE POST (CUSTOM CONFIRMATION POPUP) ──────────────────────────────────
  const promptDeletePost = (post) => {
    setDeleteConfirmState({ isOpen: true, post });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmState.post) return;
    const postId = deleteConfirmState.post.id;
    try {
      const res = await communityAPI.deletePost(postId);
      if (res.data?.success) {
        toast.success("Discussion removed successfully.");
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        fetchStats();
      }
    } catch (err) {
      toast.error("Could not delete discussion.");
    } finally {
      setDeleteConfirmState({ isOpen: false, post: null });
    }
  };

  // ─── SHARE POST (OPENS CUSTOM LUXURY SHARE MODAL) ────────────────────────────
  const handleSharePost = (post) => {
    setShareModalState({
      isOpen: true,
      post,
      copied: false,
    });
  };

  const getShareUrl = (post) => {
    if (!post) return window.location.href;
    const url = new URL(window.location.origin + window.location.pathname);
    url.searchParams.set("discussion", post.id);
    return url.toString();
  };

  const handleCopyShareLink = (post) => {
    const shareUrl = getShareUrl(post);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      setShareModalState((prev) => ({ ...prev, copied: true }));
      toast.success("Discussion link copied to clipboard!");
      setTimeout(() => {
        setShareModalState((prev) => ({ ...prev, copied: false }));
      }, 2500);
    } else {
      toast.info(`Discussion link: ${shareUrl}`);
    }
  };

  const handleNativeDeviceShare = async (post) => {
    const shareUrl = getShareUrl(post);
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: `Check out this discussion on Divyyog: "${post.title}"`,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          console.warn("Native share error:", err);
        }
      }
    } else {
      handleCopyShareLink(post);
    }
  };

  // ─── TIME FORMATTER ──────────────────────────────────────────────────────────
  const formatTimeAgo = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffSecs = Math.floor((now - date) / 1000);
      if (diffSecs < 60) return "Just now";
      const diffMins = Math.floor(diffSecs / 60);
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 30) return `${diffDays}d ago`;
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
      return "Recently";
    }
  };

  return (
    <div className="relative overflow-hidden bg-[#FFF8EE] min-h-screen pb-28">
      
      {/* ── 1. CUSTOM TOPIC-BASED HERO SECTION (PHOTOREALISTIC GENERATED BG) ── */}
      <section className="relative w-full min-h-screen min-h-[100dvh] md:min-h-[580px] lg:min-h-[640px] flex items-center justify-center overflow-hidden">
        {/* Topic Generated Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/community_hero_bg.jpg"
            alt="Universal Sangha Yogic Gathering"
            className="w-full h-full object-cover scale-[1.02]"
            style={{ filter: "brightness(0.40) saturate(0.95)" }}
          />
          {/* Subtle warm amber radial vignettes */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#120700]/75 via-[#120700]/60 to-[#120700]/80" />
          <div className="absolute top-[30%] left-[25%] w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle,rgba(242,154,12,0.30)_0%,transparent_70%)] pointer-events-none" />
          <div className="absolute bottom-[10%] right-[15%] w-[420px] h-[420px] rounded-full bg-[radial-gradient(circle,rgba(104,138,94,0.22)_0%,transparent_70%)] pointer-events-none" />
        </div>

        {/* Floating Particles inside Hero */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/20 animate-float-particle pointer-events-none hidden md:block z-10"
            style={{
              width: `${6 + i * 2}px`,
              height: `${6 + i * 2}px`,
              top: `${20 + i * 11}%`,
              left: `${15 + (i * 14) % 70}%`,
              animationDelay: `${i * 0.5}s`,
              "--duration": `${6 + i}s`,
            }}
          />
        ))}

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1150px] mx-auto px-5 sm:px-6 text-center pt-28 pb-24 md:pt-20 md:pb-16 min-h-[100dvh] md:min-h-0 flex flex-col items-center justify-center md:block">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 sm:gap-2.5 px-4 sm:px-5 py-2.5 sm:py-2 rounded-full bg-white/10 border border-white/20 mb-7 sm:mb-6 backdrop-blur-xl shadow-lg max-w-full"
          >
            <FaGlobe className="text-[#F29A0C] shrink-0" size={12} />
            <span className="tracking-[1.5px] sm:tracking-[3px] uppercase text-[9px] sm:text-[10px] font-bold text-white/90">
              DIVYYOG CIRCLE • UNIVERSAL SANGHA
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-[clamp(30px,9.4vw,40px)] sm:text-[40px] md:text-[72px] lg:text-[80px] leading-[1.14] sm:leading-[1.08] tracking-[-0.5px] sm:tracking-[-1.5px] font-extrabold text-white font-poppins"
          >
            Belong to the <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F29A0C] via-[#FFB74D] to-[#F29A0C]">
              Universal Sangha
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-white/80 text-[15px] sm:text-base md:text-[20px] leading-[27px] sm:leading-[34px] mt-6 max-w-[760px] mx-auto font-normal"
          >
            A sacred digital sanctuary for authentic seekers worldwide. Share your healing breakthroughs, exchange
            wholesome Ayurvedic wisdom, seek practice alignment, and grow together with our master teachers.
          </motion.p>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 sm:mt-8 w-full sm:w-auto flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3.5 sm:gap-4"
          >
            <button
              onClick={() => {
                if (!auth.loggedIn) {
                  toast.info("Please sign in to publish a discussion.");
                  navigate("/login");
                } else {
                  setIsModalOpen(true);
                }
              }}
              className="px-8 py-4 sm:py-3.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#E05300] text-white font-bold text-xs uppercase tracking-wider shadow-[0_8px_30px_rgba(242,154,12,0.4)] hover:shadow-[0_12px_40px_rgba(242,154,12,0.6)] hover:scale-105 active:scale-95 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
            >
              <FaPlus size={11} />
              <span>Compose Discussion</span>
            </button>

            <a
              href="#circle-feed"
              className="px-8 py-4 sm:py-3.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/25 text-white font-semibold text-xs uppercase tracking-wider backdrop-blur-md transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Explore Conversations</span>
              <FaChevronDown size={10} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── 2. HIGH-END 3D METRIC CARDS (100% REAL API DRIVEN) ── */}
      <div className="relative z-20 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-16 -mt-14">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6"
        >
          {[
            {
              tag: "GLOBAL PRACTITIONERS",
              icon: <FaUsers size={22} />,
              number: stats.activeMembers,
              title: "Registered Seekers",
              desc: "Practitioners across the platform",
            },
            {
              tag: "SACRED CIRCLES",
              icon: <FaComments size={22} />,
              number: stats.totalDiscussions,
              title: "Active Discussions",
              desc: "Published community threads",
            },
            {
              tag: "SANGHA ANSWERS",
              icon: <FaCalendarCheck size={22} />,
              number: stats.totalReplies,
              title: "Shared Responses",
              desc: "Guidance & practitioner replies",
            },
            {
              tag: "COMMUNITY PRAISE",
              icon: <FaHeart size={22} />,
              number: stats.totalLikes,
              title: "Practice Blessings",
              desc: "Community loves & praises",
            },
          ].map((item, idx) => (
            <motion.div key={idx} variants={fadeInUp} className="flex">
              <MagCard className="group relative w-full min-w-0 overflow-hidden rounded-[22px] sm:rounded-[32px] border border-[#F29A0C]/35 hover:border-[#F29A0C] bg-gradient-to-b from-white/95 via-white/90 to-[#FFFDF9]/85 backdrop-blur-3xl p-4 sm:p-6 md:p-7 shadow-[0_15px_40px_rgba(0,0,0,0.03)] hover:shadow-[0_25px_70px_rgba(242,154,12,0.25)] transition-all duration-500 flex flex-col justify-between cursor-pointer">
                
                {/* Top golden shimmer accent line */}
                <div className="absolute top-0 left-0 right-0 h-[2.5px] bg-gradient-to-r from-transparent via-[#F29A0C] to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />

                {/* Ambient gold bloom on hover */}
                <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-[#F29A0C]/10 blur-2xl group-hover:scale-150 transition-transform duration-700 pointer-events-none" />

                <div>
                  {/* Glowing Icon Insignia Box */}
                  <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
                    <div className="w-10 h-10 sm:w-13 sm:h-13 shrink-0 rounded-2xl bg-gradient-to-br from-[#FFF3E0] via-[#FFE0B2] to-[#FFCC80] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C] shadow-[0_4px_16px_rgba(242,154,12,0.2)] group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      {item.icon}
                    </div>
                    <span className="hidden sm:inline text-[9px] font-bold tracking-[2px] uppercase text-[#F29A0C]/85 bg-[#FFF3E0] px-2.5 py-1 rounded-full border border-[#F4D9B5]/70">
                      {item.tag}
                    </span>
                  </div>
                  
                  {/* Real Dynamic High-Impact Number */}
                  <h3 className="text-[26px] sm:text-4xl lg:text-[42px] font-black text-[#111111] font-poppins leading-none tracking-tight group-hover:text-[#F29A0C] transition-colors duration-300">
                    {item.number}
                  </h3>
                  
                  <h4 className="text-[13px] sm:text-[15px] leading-snug font-bold text-[#1F1F1F] mt-2 sm:mt-2.5 mb-1 group-hover:text-[#F29A0C] transition-colors">
                    {item.title}
                  </h4>
                </div>

                <div>
                  <p className="text-[#666666] text-[11px] sm:text-xs font-normal leading-relaxed mt-1.5 sm:mt-2">
                    {item.desc}
                  </p>

                  {/* Live sync indicator */}
                  <div className="flex items-center gap-1.5 mt-2.5 sm:mt-3 pt-2.5 sm:pt-3 border-t border-[#F4D9B5]/40 text-[10px] font-medium text-[#888888]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                    <span>Real-time Live Data</span>
                  </div>
                </div>
              </MagCard>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* ── 3. COMMUNITY FEED & INTERACTIVE STUDIO ── */}
      <div id="circle-feed" className="relative z-20 max-w-[1400px] mx-auto px-6 lg:px-16 mt-16 scroll-mt-24">
        
        {/* Filter Bar & Controls */}
        <div className="relative z-40 rounded-[32px] border border-[#F4D9B5] bg-white/80 backdrop-blur-2xl p-6 shadow-[0_20px_60px_rgba(0,0,0,0.03)] mb-10">
          
          {/* Category Pills with Real Dynamic Count from API */}
          <div className="flex items-center gap-2.5 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-[#F4D9B5]">
            {CATEGORIES.map((cat) => {
              const Icon = cat.Icon;
              const isSelected = selectedCategory === cat.key;
              const postCount = cat.key === "All" 
                ? stats.totalDiscussions 
                : (stats.categoryBreakdown[cat.key] || 0);

              return (
                <button
                  key={cat.key}
                  onClick={() => {
                    setSelectedCategory(cat.key);
                    setViewMyPostsOnly(false);
                  }}
                  className={`px-4 py-2.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-300 flex items-center gap-2 cursor-pointer ${
                    isSelected && !viewMyPostsOnly
                      ? "bg-[#F29A0C] text-white shadow-[0_4px_16px_rgba(242,154,12,0.4)]"
                      : "bg-[#FFF8EE] text-[#555555] border border-[#F4D9B5]/80 hover:border-[#F29A0C]/60 hover:text-[#111111]"
                  }`}
                >
                  <Icon size={12} className={isSelected && !viewMyPostsOnly ? "text-white" : "text-[#F29A0C]"} />
                  <span>{cat.label}</span>
                  <span className={`text-[10px] px-2 py-0.2 rounded-full font-bold ${
                    isSelected && !viewMyPostsOnly ? "bg-white/20 text-white" : "bg-[#F4D9B5]/40 text-[#888888]"
                  }`}>
                    {postCount}
                  </span>
                </button>
              );
            })}

            {/* My Discussions Tab (If Logged In) */}
            {auth.loggedIn && (
              <button
                onClick={() => setViewMyPostsOnly(!viewMyPostsOnly)}
                className={`px-4 py-2.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-300 flex items-center gap-2 cursor-pointer ${
                  viewMyPostsOnly
                    ? "bg-[#120700] text-[#F29A0C] border border-[#F29A0C] shadow-md"
                    : "bg-[#FFF8EE] text-[#555555] border border-[#F4D9B5]/80 hover:border-[#F29A0C]/60"
                }`}
              >
                <FaUserCheck size={12} className="text-[#F29A0C]" />
                <span>My Discussions</span>
              </button>
            )}
          </div>

          {/* Search & Custom Sort Row */}
          <div className="relative z-50 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-[#F4D9B5]/40 mt-2">
            
            {/* Search Input with standard icons */}
            <div className="relative w-full sm:max-w-md">
              <FaSearch size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#888888]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search discussions by keyword, author, or question..."
                className="w-full h-[48px] pl-11 pr-10 rounded-2xl border border-[#F4D9B5] bg-white text-xs font-medium text-[#111111] placeholder:text-[#888888] focus:outline-none focus:border-[#F29A0C] focus:ring-2 focus:ring-[#F29A0C]/20 transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#999999] hover:text-[#111111] cursor-pointer"
                >
                  <FaTimes size={12} />
                </button>
              )}
            </div>

            {/* Custom Styled Dropdown */}
            <div className="w-full sm:w-auto flex items-center justify-end">
              <CustomSortDropdown selected={selectedSort} onChange={setSelectedSort} />
            </div>

          </div>
        </div>

        {/* 2-Column Responsive Layout */}
        <div className="relative z-10 grid lg:grid-cols-[1.75fr_1.15fr] gap-10 items-start">
          
          {/* ── LEFT COLUMN: MAIN DISCUSSION STREAM ── */}
          <div className="space-y-6">
            
            {/* Loading Skeletons */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="animate-pulse rounded-[32px] border border-[#F4D9B5]/70 bg-white/70 p-7 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#F4D9B5]/40" />
                      <div className="space-y-2">
                        <div className="w-32 h-3.5 bg-[#F4D9B5]/40 rounded-md" />
                        <div className="w-20 h-2.5 bg-[#F4D9B5]/30 rounded-md" />
                      </div>
                    </div>
                    <div className="w-3/4 h-5 bg-[#F4D9B5]/50 rounded-md" />
                    <div className="w-full h-12 bg-[#F4D9B5]/20 rounded-md" />
                  </div>
                ))}
              </div>
            ) : displayedPosts.length === 0 ? (
              /* Empty State */
              <div className="rounded-[36px] border border-[#F4D9B5] bg-white/80 backdrop-blur-2xl p-12 text-center shadow-sm">
                <div className="w-16 h-16 rounded-3xl bg-[#FFF3E0] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C] mx-auto mb-5 shadow-sm">
                  <GiLotus size={32} />
                </div>
                <h3 className="text-xl font-bold text-[#111111] font-poppins">No Discussions Found</h3>
                <p className="text-[#666666] text-xs max-w-md mx-auto mt-2 leading-relaxed">
                  {viewMyPostsOnly 
                    ? "You haven't created any discussions yet. Share your thoughts using the composer on the right!"
                    : "There are no discussions matching this filter yet. Be the first to share your practice reflection with the Sangha!"}
                </p>
                <button
                  onClick={() => {
                    if (!auth.loggedIn) {
                      navigate("/login");
                    } else {
                      setIsModalOpen(true);
                    }
                  }}
                  className="mt-6 px-6 py-3 rounded-full bg-[#F29A0C] text-white font-bold text-xs uppercase tracking-wider shadow-md hover:scale-105 transition-transform cursor-pointer"
                >
                  Start First Discussion
                </button>
              </div>
            ) : (
              /* Discussions List */
              displayedPosts.map((post) => {
                const isLiked = likedPosts.has(post.id);
                const isCommentsOpen = expandedPostId === post.id;
                const comments = commentsMap[post.id] || [];
                const isThreadLoading = commentsLoading[post.id];
                const isAuthorOrStaff = auth.loggedIn && (
                  (auth.user?.id && post.userId === auth.user.id) ||
                  auth.role === "SuperAdmin" || auth.role === "Trainer"
                );

                return (
                  <motion.div
                    key={post.id}
                    id={`discussion-${post.id}`}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    className="group rounded-[32px] border border-[#F4D9B5] bg-white/85 backdrop-blur-2xl p-6 md:p-7 shadow-[0_15px_40px_rgba(0,0,0,0.02)] hover:border-[#F29A0C]/60 hover:shadow-[0_20px_50px_rgba(242,154,12,0.1)] transition-all duration-300"
                  >
                    {/* Top Header: Author & Meta */}
                    <div className="flex items-center justify-between gap-4 mb-4">
                      
                      <div className="flex items-center gap-3">
                        {post.authorAvatar ? (
                          <img
                            src={post.authorAvatar}
                            alt={post.authorName}
                            className="w-11 h-11 rounded-2xl object-cover border border-[#F29A0C]/40 shadow-sm"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#FFF3E0] to-[#FFE0B2] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C] font-bold text-sm shadow-sm">
                            {post.authorName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-bold text-[#111111]">{post.authorName}</h4>
                            {/* Role Badge with standard icons */}
                            {post.authorRole === "Founder" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#120700] text-[#F29A0C] text-[10px] font-bold uppercase tracking-wider border border-[#F29A0C]/40">
                                <FaCrown size={9} />
                                Founder
                              </span>
                            ) : post.authorRole === "Trainer" ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#E8F5E9] text-[#2E7D32] text-[10px] font-bold uppercase tracking-wider border border-[#A5D6A7]">
                                <FaCheckCircle size={9} />
                                Trainer
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-[#FFF3E0] text-[#E65100] text-[10px] font-semibold">
                                {post.authorRole || "Member"}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-[#888888] font-normal flex items-center gap-2 mt-0.5">
                            <span>{formatTimeAgo(post.createdAt)}</span>
                            <span>•</span>
                            <span className="text-[#F29A0C] font-medium">{post.category}</span>
                          </p>
                        </div>
                      </div>

                      {/* Right Meta: Pinned & Delete Action */}
                      <div className="flex items-center gap-2">
                        {post.isPinned && (
                          <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FFF3E0] border border-[#F29A0C]/40 text-[#F29A0C] text-[10px] font-bold uppercase tracking-wider">
                            <FaCrown size={10} />
                            <span>Pinned</span>
                          </div>
                        )}

                        {isAuthorOrStaff && (
                          <button
                            type="button"
                            onClick={() => promptDeletePost(post)}
                            className="w-8 h-8 rounded-full hover:bg-[#FFEBEB] text-[#999999] hover:text-[#E53935] flex items-center justify-center transition-colors cursor-pointer"
                            title="Delete Discussion"
                          >
                            <FaTrash size={12} />
                          </button>
                        )}
                      </div>

                    </div>

                    {/* Post Content */}
                    <h3 className="text-lg md:text-[19px] font-bold text-[#111111] font-poppins leading-snug group-hover:text-[#F29A0C] transition-colors duration-300">
                      {post.title}
                    </h3>

                    <p className="text-[#555555] text-xs md:text-[13px] leading-[23px] font-normal mt-3 whitespace-pre-line">
                      {post.content}
                    </p>

                    {/* Action Bar */}
                    <div className="flex items-center justify-between pt-5 mt-5 border-t border-[#F4D9B5]/50 text-xs text-[#666666]">
                      
                      {/* Left: Like & Comment Buttons */}
                      <div className="flex items-center gap-4">
                        {/* Like Button */}
                        <button
                          type="button"
                          onClick={() => handleToggleLike(post.id)}
                          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                            isLiked
                              ? "bg-[#FFEBEB] text-[#E53935] font-bold border border-[#FFCDD2]"
                              : "bg-[#FFF8EE] hover:bg-[#FFF3E0] text-[#666666] hover:text-[#E53935] border border-[#F4D9B5]/60"
                          }`}
                        >
                          {isLiked ? <FaHeart size={12} className="text-[#E53935]" /> : <FaRegHeart size={12} />}
                          <span>{post.likesCount}</span>
                        </button>

                        {/* Comment Drawer Toggle Button */}
                        <button
                          type="button"
                          onClick={() => handleToggleComments(post.id)}
                          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border transition-all duration-300 cursor-pointer ${
                            isCommentsOpen
                              ? "bg-[#FFF3E0] border-[#F29A0C] text-[#F29A0C] font-bold"
                              : "bg-[#FFF8EE] border-[#F4D9B5]/60 text-[#666666] hover:text-[#111111] hover:border-[#F29A0C]/50"
                          }`}
                        >
                          <FaRegCommentDots size={13} />
                          <span>{post.commentsCount} Replies</span>
                        </button>
                      </div>

                      {/* Right: Views & Share */}
                      <div className="flex items-center gap-4 text-[#888888]">
                        <span className="inline-flex items-center gap-1 text-[11px]">
                          <FiEye size={13} />
                          <span>{post.viewsCount}</span>
                        </span>

                        <button
                          type="button"
                          onClick={() => handleSharePost(post)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFF8EE] hover:bg-[#FFF3E0] border border-[#F4D9B5]/70 hover:border-[#F29A0C]/60 text-[#666666] hover:text-[#F29A0C] transition-all cursor-pointer text-xs font-semibold shadow-2xs"
                          title="Share Discussion"
                        >
                          <FaShareAlt size={11} />
                          <span>Share</span>
                        </button>
                      </div>

                    </div>

                    {/* ── EXPANDABLE INLINE COMMENTS DRAWER ── */}
                    <AnimatePresence>
                      {isCommentsOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden pt-5 mt-4 border-t border-[#F4D9B5]/40"
                        >
                          <div className="space-y-3 mb-4">
                            {isThreadLoading ? (
                              <p className="text-xs text-[#888888] italic">Loading replies...</p>
                            ) : comments.length === 0 ? (
                              <p className="text-xs text-[#888888] italic py-2">
                                No replies yet. Be the first to share your thoughts!
                              </p>
                            ) : (
                              comments.map((comment) => (
                                <div
                                  key={comment.id}
                                  className="p-3.5 rounded-2xl bg-[#FFF8EE]/70 border border-[#F4D9B5]/60 flex items-start gap-3"
                                >
                                  {comment.authorAvatar ? (
                                    <img
                                      src={comment.authorAvatar}
                                      alt={comment.authorName}
                                      className="w-7 h-7 rounded-xl object-cover shrink-0 mt-0.5 border border-[#F29A0C]/30"
                                    />
                                  ) : (
                                    <div className="w-7 h-7 rounded-xl bg-[#FFF3E0] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C] font-bold text-[10px] shrink-0 mt-0.5">
                                      {comment.authorName.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-xs font-bold text-[#111111]">
                                          {comment.authorName}
                                        </span>
                                        {comment.authorRole === "Founder" && (
                                          <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-[#120700] text-[#F29A0C]">
                                            Founder
                                          </span>
                                        )}
                                        {comment.authorRole === "Trainer" && (
                                          <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-[#E8F5E9] text-[#2E7D32]">
                                            Trainer
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-[10px] text-[#999999]">
                                        {formatTimeAgo(comment.createdAt)}
                                      </span>
                                    </div>
                                    <p className="text-xs text-[#444444] font-normal mt-1 leading-relaxed">
                                      {comment.content}
                                    </p>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>

                          {/* Reply Input Box */}
                          <div className="flex items-center gap-2 pt-2">
                            <input
                              type="text"
                              value={replyTextMap[post.id] || ""}
                              onChange={(e) =>
                                setReplyTextMap((prev) => ({ ...prev, [post.id]: e.target.value }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSubmitReply(post.id);
                                }
                              }}
                              placeholder={
                                auth.loggedIn
                                  ? "Write a mindful response..."
                                  : "Sign in to reply to this discussion..."
                              }
                              className="flex-1 h-[42px] px-4 rounded-xl border border-[#F4D9B5] bg-white text-xs text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#F29A0C]"
                            />
                            <button
                              type="button"
                              disabled={submittingReply[post.id]}
                              onClick={() => handleSubmitReply(post.id)}
                              className="h-[42px] px-4 rounded-xl bg-[#F29A0C] hover:bg-[#E05300] text-white text-xs font-bold flex items-center gap-1.5 transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
                            >
                              <FaPaperPlane size={11} />
                              <span>{submittingReply[post.id] ? "Sending..." : "Reply"}</span>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                  </motion.div>
                );
              })
            )}

          </div>

          {/* ── RIGHT COLUMN: USER DISCUSSION CREATOR & LIVE API PANELS ── */}
          <div className="space-y-6">
            
            {/* 1. DIRECT INLINE DISCUSSION CREATOR STUDIO */}
            <div className="rounded-[32px] border border-[#F29A0C]/40 bg-gradient-to-br from-white/95 to-[#FFF8EE] backdrop-blur-2xl p-7 shadow-[0_20px_50px_rgba(242,154,12,0.12)]">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#F29A0C] to-[#E05300] flex items-center justify-center text-white shadow-md">
                  <FaPenFancy size={16} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#111111] font-poppins">Publish a Discussion</h3>
                  <p className="text-[11px] text-[#777777]">Share queries, recipes, or milestones</p>
                </div>
              </div>

              <form onSubmit={handleCreatePost} className="space-y-4">
                {/* Topic Selector via Custom Dropdown */}
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1.5">
                    Topic Category
                  </label>
                  <CustomCategorySelect
                    value={createForm.category}
                    onChange={(cat) => setCreateForm((prev) => ({ ...prev, category: cat }))}
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1.5">
                    Discussion Title
                  </label>
                  <input
                    type="text"
                    value={createForm.title}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Relieving shoulder tightness after desk work..."
                    className="w-full h-[46px] px-4 rounded-2xl border border-[#F4D9B5] bg-white text-xs font-medium text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#F29A0C] transition-all"
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1.5">
                    Your Reflection or Question
                  </label>
                  <textarea
                    value={createForm.content}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, content: e.target.value }))}
                    placeholder="Write your experience, ask advice, or post details for the community..."
                    rows={4}
                    className="w-full p-4 rounded-2xl border border-[#F4D9B5] bg-white text-xs font-medium text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#F29A0C] transition-all resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#E05300] text-white font-bold text-xs uppercase tracking-wider shadow-md hover:shadow-lg hover:scale-102 active:scale-98 disabled:opacity-50 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <FaPaperPlane size={11} />
                  <span>{isSubmitting ? "Publishing..." : "Post to Circle"}</span>
                </button>
              </form>
            </div>

            {/* 2. REAL ACTIVE COMMUNITY VOICES (100% FROM API) */}
            {stats.recentContributors && stats.recentContributors.length > 0 && (
              <div className="rounded-[32px] border border-[#F4D9B5] bg-white/80 backdrop-blur-2xl p-7 shadow-[0_20px_50px_rgba(0,0,0,0.02)]">
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-8 h-8 rounded-xl bg-[#FFF3E0] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C]">
                    <FaFireAlt size={13} />
                  </div>
                  <h3 className="text-base font-bold text-[#111111] font-poppins">Recent Active Voices</h3>
                </div>

                <div className="space-y-3">
                  {stats.recentContributors.map((user, i) => (
                    <div
                      key={i}
                      className="p-3 rounded-2xl bg-[#FFF8EE]/80 border border-[#F4D9B5]/60 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.name}
                            className="w-9 h-9 rounded-xl object-cover border border-[#F29A0C]/40"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-xl bg-[#FFF3E0] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C] font-bold text-xs">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-bold text-[#111111]">{user.name}</p>
                          <p className="text-[10px] text-[#777777] font-medium">{user.role}</p>
                        </div>
                      </div>

                      <span className="text-[10px] font-semibold text-[#F29A0C] bg-[#FFF3E0] px-2.5 py-1 rounded-full">
                        Contributor
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 3. LIVE TOPICS BREAKDOWN (100% REAL DATABASE COUNTS) */}
            <div className="rounded-[32px] border border-[#F4D9B5] bg-white/80 backdrop-blur-2xl p-7 shadow-[0_20px_50px_rgba(0,0,0,0.02)]">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-8 h-8 rounded-xl bg-[#FFF3E0] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C]">
                  <GiLotus size={16} />
                </div>
                <h3 className="text-base font-bold text-[#111111] font-poppins">Live Topic Breakdown</h3>
              </div>

              <div className="space-y-2.5">
                {CATEGORIES.filter((c) => c.key !== "All").map((cat) => {
                  const Icon = cat.Icon;
                  const count = stats.categoryBreakdown[cat.key] || 0;
                  const isCurrent = selectedCategory === cat.key;

                  return (
                    <button
                      key={cat.key}
                      onClick={() => {
                        setSelectedCategory(cat.key);
                        setViewMyPostsOnly(false);
                      }}
                      className={`w-full p-3 rounded-2xl border transition-all duration-200 flex items-center justify-between cursor-pointer ${
                        isCurrent
                          ? "bg-[#F29A0C] text-white border-[#F29A0C] font-bold shadow-sm"
                          : "bg-[#FFF8EE]/70 hover:bg-[#FFF3E0] border-[#F4D9B5]/60 text-[#333333]"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <Icon size={12} className={isCurrent ? "text-white" : "text-[#F29A0C]"} />
                        <span className="text-xs">{cat.label}</span>
                      </div>
                      <span
                        className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${
                          isCurrent ? "bg-white/20 text-white" : "bg-white text-[#F29A0C] border border-[#F4D9B5]"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* ── CREATE DISCUSSION MODAL ── */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.25 }}
              className="relative w-full max-w-xl rounded-[36px] border border-[#F4D9B5] bg-white p-7 sm:p-9 shadow-[0_30px_90px_rgba(0,0,0,0.25)] max-h-[90vh] overflow-y-auto"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 w-9 h-9 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] flex items-center justify-center text-[#555555] hover:text-[#111111] hover:border-[#F29A0C] transition-all cursor-pointer"
              >
                <FiX size={16} />
              </button>

              {/* Modal Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-2xl bg-[#FFF3E0] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C]">
                  <GiLotus size={20} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#111111] font-poppins">Compose Discussion</h3>
                  <p className="text-xs text-[#777777]">Post directly to the Universal Sangha</p>
                </div>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleCreatePost} className="space-y-5">
                
                {/* Category Custom Dropdown */}
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-2">
                    Topic Category
                  </label>
                  <CustomCategorySelect
                    value={createForm.category}
                    onChange={(cat) => setCreateForm((prev) => ({ ...prev, category: cat }))}
                  />
                </div>

                {/* Title Input */}
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-2">
                    Discussion Title
                  </label>
                  <input
                    type="text"
                    value={createForm.title}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. How to maintain consistent morning sadhana?"
                    maxLength={250}
                    className="w-full h-[50px] px-4 rounded-2xl border border-[#F4D9B5] bg-white text-xs font-medium text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#F29A0C] focus:ring-2 focus:ring-[#F29A0C]/20 transition-all"
                  />
                </div>

                {/* Content Textarea */}
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-2">
                    Your Thoughts or Question
                  </label>
                  <textarea
                    value={createForm.content}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, content: e.target.value }))}
                    placeholder="Provide context, describe your experience, or ask detailed questions..."
                    rows={6}
                    maxLength={4000}
                    className="w-full p-4 rounded-2xl border border-[#F4D9B5] bg-white text-xs font-medium text-[#111111] placeholder:text-[#999999] focus:outline-none focus:border-[#F29A0C] focus:ring-2 focus:ring-[#F29A0C]/20 transition-all resize-none"
                  />
                </div>

                {/* Submit Buttons */}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-6 py-3 rounded-full text-xs font-semibold text-[#666666] hover:bg-[#FFF8EE] transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-3.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#E05300] text-white font-bold text-xs uppercase tracking-wider shadow-md hover:shadow-lg disabled:opacity-50 transition-all cursor-pointer flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <span>Publishing...</span>
                    ) : (
                      <>
                        <FaPaperPlane size={11} />
                        <span>Publish Discussion</span>
                      </>
                    )}
                  </button>
                </div>

              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── 5. CUSTOM LUXURY DELETE CONFIRMATION MODAL ── */}
      <AnimatePresence>
        {deleteConfirmState.isOpen && (
          <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/65 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 15 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-md bg-white rounded-[32px] border border-[#F4D9B5] p-6 sm:p-8 shadow-[0_25px_70px_rgba(0,0,0,0.25)] overflow-hidden"
            >
              {/* Top Accent Line */}
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#E53935] to-transparent opacity-80" />

              {/* Close Button */}
              <button
                type="button"
                onClick={() => setDeleteConfirmState({ isOpen: false, post: null })}
                className="absolute top-5 right-5 w-8 h-8 rounded-full bg-[#FFF8EE] border border-[#F4D9B5]/80 hover:bg-[#FFEBEB] text-[#777777] hover:text-[#E53935] flex items-center justify-center transition-colors cursor-pointer"
              >
                <FaTimes size={13} />
              </button>

              {/* Header with Icon Badge */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#FFEBEB] border border-[#FFCDD2] flex items-center justify-center text-[#E53935] shrink-0 shadow-[0_4px_14px_rgba(229,57,53,0.18)]">
                  <FaTrash size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#111111] font-poppins">
                    Delete Discussion
                  </h3>
                  <p className="text-xs text-[#888888] mt-0.5">
                    Irreversible action warning
                  </p>
                </div>
              </div>

              {/* Discussion Title Preview */}
              {deleteConfirmState.post && (
                <div className="bg-[#FFF8EE] border border-[#F4D9B5]/70 rounded-2xl p-3.5 mb-4">
                  <p className="text-[11px] uppercase tracking-wider text-[#F29A0C] font-bold mb-1">
                    Discussion Title
                  </p>
                  <p className="text-xs font-semibold text-[#222222] line-clamp-2">
                    {deleteConfirmState.post.title}
                  </p>
                </div>
              )}

              {/* Explanation Text */}
              <p className="text-xs text-[#555555] leading-relaxed mb-6">
                Are you sure you want to permanently delete this discussion? All community comments, views, and reactions will be permanently removed.
              </p>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmState({ isOpen: false, post: null })}
                  className="px-5 py-2.5 rounded-full text-xs font-semibold text-[#666666] hover:bg-[#FFF8EE] border border-[#F4D9B5]/60 hover:border-[#F4D9B5] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#E53935] to-[#C62828] hover:from-[#D32F2F] hover:to-[#B71C1C] text-white font-bold text-xs uppercase tracking-wider shadow-[0_4px_14px_rgba(229,57,53,0.3)] hover:shadow-lg transition-all cursor-pointer flex items-center gap-2"
                >
                  <FaTrash size={11} />
                  <span>Confirm Delete</span>
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── 6. CUSTOM LUXURY SHARE DISCUSSION MODAL ── */}
      <AnimatePresence>
        {shareModalState.isOpen && shareModalState.post && (
          <div className="fixed inset-0 z-[260] flex items-center justify-center p-4 bg-black/65 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 15 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative w-full max-w-lg bg-white rounded-[32px] border border-[#F4D9B5] p-6 sm:p-8 shadow-[0_25px_70px_rgba(0,0,0,0.25)] overflow-hidden"
            >
              {/* Top Golden Shimmer Line */}
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#F29A0C] to-transparent opacity-90" />

              {/* Close Button */}
              <button
                type="button"
                onClick={() => setShareModalState({ isOpen: false, post: null, copied: false })}
                className="absolute top-5 right-5 w-8 h-8 rounded-full bg-[#FFF8EE] border border-[#F4D9B5]/80 hover:bg-[#FFE0B2] text-[#777777] hover:text-[#111111] flex items-center justify-center transition-colors cursor-pointer border-none"
              >
                <FaTimes size={13} />
              </button>

              {/* Header */}
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFF3E0] to-[#FFE0B2] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C] shrink-0 shadow-[0_4px_14px_rgba(242,154,12,0.2)]">
                  <FaShareAlt size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#111111] font-poppins">
                    Share Discussion
                  </h3>
                  <p className="text-xs text-[#777777] mt-0.5">
                    Share wisdom with your yoga circle and community
                  </p>
                </div>
              </div>

              {/* Discussion Preview Pill */}
              <div className="bg-[#FFF8EE] border border-[#F4D9B5]/70 rounded-2xl p-4 mb-6">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#F29A0C] bg-white px-2.5 py-0.5 rounded-full border border-[#F4D9B5]/80">
                    {shareModalState.post.category}
                  </span>
                  <span className="text-[11px] text-[#888888] font-medium">
                    By {shareModalState.post.authorName}
                  </span>
                </div>
                <h4 className="text-xs sm:text-[13px] font-bold text-[#111111] line-clamp-2 leading-snug">
                  {shareModalState.post.title}
                </h4>
              </div>

              {/* Social Channels Label */}
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#888888] mb-3">
                Share Directly To
              </p>

              {/* Direct Share Options Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-6">
                
                {/* WhatsApp */}
                <a
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out this discussion on Divyyog: "${shareModalState.post.title}"\n${getShareUrl(shareModalState.post)}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 p-3 rounded-2xl bg-[#E8F8EE] border border-[#C3ECD1] hover:bg-[#D4F4DF] text-[#1B8343] transition-all no-underline font-semibold text-xs shadow-2xs hover:scale-[1.02]"
                >
                  <div className="w-8 h-8 rounded-xl bg-[#25D366] text-white flex items-center justify-center shrink-0 shadow-xs">
                    <FaWhatsapp size={16} />
                  </div>
                  <span>WhatsApp</span>
                </a>

                {/* Facebook */}
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl(shareModalState.post))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 p-3 rounded-2xl bg-[#EEF4FF] border border-[#C6DCFD] hover:bg-[#DCE9FE] text-[#1849A9] transition-all no-underline font-semibold text-xs shadow-2xs hover:scale-[1.02]"
                >
                  <div className="w-8 h-8 rounded-xl bg-[#1877F2] text-white flex items-center justify-center shrink-0 shadow-xs">
                    <FaFacebookF size={14} />
                  </div>
                  <span>Facebook</span>
                </a>

                {/* LinkedIn */}
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getShareUrl(shareModalState.post))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 p-3 rounded-2xl bg-[#EFF8FF] border border-[#B2DDFF] hover:bg-[#D1E9FF] text-[#175CD3] transition-all no-underline font-semibold text-xs shadow-2xs hover:scale-[1.02]"
                >
                  <div className="w-8 h-8 rounded-xl bg-[#0A66C2] text-white flex items-center justify-center shrink-0 shadow-xs">
                    <FaLinkedinIn size={14} />
                  </div>
                  <span>LinkedIn</span>
                </a>

                {/* Telegram */}
                <a
                  href={`https://t.me/share/url?url=${encodeURIComponent(getShareUrl(shareModalState.post))}&text=${encodeURIComponent(`Check out this discussion on Divyyog: "${shareModalState.post.title}"`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 p-3 rounded-2xl bg-[#F0F9FF] border border-[#BAE6FD] hover:bg-[#E0F2FE] text-[#026AA2] transition-all no-underline font-semibold text-xs shadow-2xs hover:scale-[1.02]"
                >
                  <div className="w-8 h-8 rounded-xl bg-[#229ED9] text-white flex items-center justify-center shrink-0 shadow-xs">
                    <FaTelegramPlane size={14} />
                  </div>
                  <span>Telegram</span>
                </a>

                {/* Email */}
                <a
                  href={`mailto:?subject=${encodeURIComponent(`Divyyog Discussion: ${shareModalState.post.title}`)}&body=${encodeURIComponent(`Namaste,\n\nI thought you might find this yoga discussion insightful:\n\n"${shareModalState.post.title}"\n\nRead more at:\n${getShareUrl(shareModalState.post)}\n\nWarm regards`)}`}
                  className="flex items-center gap-2.5 p-3 rounded-2xl bg-[#FFF4ED] border border-[#FFD6AE] hover:bg-[#FFE6D5] text-[#C4320A] transition-all no-underline font-semibold text-xs shadow-2xs hover:scale-[1.02]"
                >
                  <div className="w-8 h-8 rounded-xl bg-[#E65100] text-white flex items-center justify-center shrink-0 shadow-xs">
                    <FaEnvelope size={13} />
                  </div>
                  <span>Email</span>
                </a>

              </div>

              {/* Native Device Share Button (If supported by OS / Browser) */}
              {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
                <div className="mb-6">
                  <button
                    type="button"
                    onClick={() => handleNativeDeviceShare(shareModalState.post)}
                    className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-[#FFF8EE] to-[#FFF3E0] hover:from-[#FFF3E0] hover:to-[#FFE0B2] border border-[#F4D9B5] text-[#111111] font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-2xs"
                  >
                    <FiShare2 size={15} className="text-[#F29A0C]" />
                    <span>Share via Device Apps (System Share Sheet)</span>
                  </button>
                </div>
              )}

              {/* Copy Link Row */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#888888] mb-2">
                  Or Copy Discussion Link
                </p>
                <div className="flex items-center gap-2 p-1.5 rounded-2xl border border-[#F4D9B5] bg-[#FFF8EE]">
                  <input
                    type="text"
                    readOnly
                    value={getShareUrl(shareModalState.post)}
                    className="flex-1 bg-transparent px-3 text-xs text-[#333333] font-medium outline-none truncate"
                  />
                  <button
                    type="button"
                    onClick={() => handleCopyShareLink(shareModalState.post)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 border-none ${
                      shareModalState.copied
                        ? "bg-[#22C55E] text-white shadow-xs"
                        : "bg-[#F29A0C] hover:bg-[#E05300] text-white shadow-xs"
                    }`}
                  >
                    {shareModalState.copied ? (
                      <>
                        <FiCheck size={14} />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <FaCopy size={12} />
                        <span>Copy Link</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
